import { PhotoTrigger } from '../photo-viewer.tsx';
import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { magicQuoteApi } from '../../api/endpoints.ts';
import { Button } from '../ui.tsx';
import type { MagicQuoteCapture } from '@shared/types/index.ts';
import { formatInstantTime } from '../../i18n/datetime.ts';

/**
 * The capture timeline: everything the walkthrough recorded, in the order it
 * happened.
 *
 * This is the artifact that makes the rest of Magic Quote trustworthy. Every
 * claim the pipeline makes points back at an entry here, and tapping that
 * evidence opens the photo or drops the player at the second the customer
 * actually said it — so an estimator never has to take an AI's word for
 * anything.
 *
 * `compact` is the in-walkthrough view: newest first, short, because during
 * the visit this is a glance to confirm "yes, that got recorded", not
 * something to read.
 */
export default function CaptureTimeline({
  sessionId,
  captures,
  speechRecognitionAvailable,
  compact = false,
  highlightIds,
  openAt,
}: {
  sessionId: string;
  captures: MagicQuoteCapture[];
  speechRecognitionAvailable: boolean;
  compact?: boolean;
  /** Captures an answer cited, drawn to the eye. */
  highlightIds?: readonly string[];
  /**
   * Capture id → seconds, when opening from an evidence link.
   *
   * `nonce` is what makes the SAME link work twice: the row scrolls when what
   * it is handed changes, and a second click on one evidence link produces an
   * identical id and offset. Without it the estimator who scrolled away to
   * read something else could not get back.
   */
  openAt?: { captureId: string; offsetSeconds: number | null; nonce?: number } | null;
}) {
  const { t } = useTranslation();
  const ordered = compact ? [...captures].reverse().slice(0, 8) : captures;

  if (ordered.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-3 py-6 text-center text-sm text-[color:var(--color-muted)]">
        {t('magicQuote.timeline.empty')}
      </p>
    );
  }

  return (
    <ol className="flex flex-col gap-2">
      {ordered.map((capture) => (
        <li key={capture.id}>
          <CaptureRow
            sessionId={sessionId}
            capture={capture}
            speechRecognitionAvailable={speechRecognitionAvailable}
            compact={compact}
            highlighted={highlightIds?.includes(capture.id) ?? false}
            opened={openAt?.captureId === capture.id}
            openAtSeconds={openAt?.captureId === capture.id ? openAt.offsetSeconds : null}
            openNonce={openAt?.captureId === capture.id ? (openAt.nonce ?? 0) : 0}
          />
        </li>
      ))}
    </ol>
  );
}

function CaptureRow({
  sessionId,
  capture,
  speechRecognitionAvailable,
  compact,
  highlighted,
  opened,
  openAtSeconds,
  openNonce,
}: {
  sessionId: string;
  capture: MagicQuoteCapture;
  speechRecognitionAvailable: boolean;
  compact: boolean;
  highlighted: boolean;
  /** True when an evidence link points at this entry. */
  opened: boolean;
  openAtSeconds: number | null;
  /** Changes on every press of the link, including presses of the same one. */
  openNonce: number;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const photoRef = useRef<HTMLDivElement | null>(null);
  const rowRef = useRef<HTMLDivElement | null>(null);
  /**
   * The audio or video element, when this row has one to seek.
   *
   * Typed as the two concrete elements rather than `HTMLMediaElement`: a ref
   * is written by the element, so the wider type is not assignable to either
   * of their `ref` props.
   */
  const mediaRef = useRef<HTMLVideoElement | HTMLAudioElement | null>(null);

  // "Show me" has to actually show it. A recording gets a start offset in its
  // own URL, but a photo had nothing at all: the row was outlined somewhere
  // down the page and the estimator was left to find it. So the entry is
  // scrolled to, and a photo is opened to full size — the point of an
  // evidence link is landing on the thing, not being told where it is.
  useEffect(() => {
    if (!opened) return;
    if (capture.type === 'photo') photoRef.current?.querySelector('button')?.click();
    // A recording keeps its position across presses: the `#t=` fragment in the
    // src only applies on load, so playing past the cited second and pressing
    // the link again would leave the estimator wherever they had got to rather
    // than back at the moment the claim came from.
    if (openAtSeconds !== null && mediaRef.current) {
      mediaRef.current.currentTime = openAtSeconds;
      void mediaRef.current.play?.().catch(() => undefined);
    }
    rowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    // The nonce is in the deps on purpose: without it this runs once per
    // capture and the second press of the same link does nothing at all.
  }, [opened, openNonce, openAtSeconds, capture.type]);

  const retry = useMutation({
    mutationFn: () => magicQuoteApi.retryCapture(sessionId, capture.id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['magic-quote', sessionId] }),
  });

  const flag = useMutation({
    mutationFn: (flagged: boolean) => magicQuoteApi.flagCapture(sessionId, capture.id, flagged),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['magic-quote', sessionId] }),
  });

  // The browser's own clock. Slicing the ISO string would print UTC, which
  // is the one time nobody standing in that house was looking at.
  const time = formatInstantTime(capture.capturedAt);
  const said = capture.transcription?.trim() || capture.text?.trim() || null;

  return (
    <div
      ref={rowRef}
      className={[
        'rounded-xl border px-3 py-2.5 transition-colors',
        highlighted || opened
          ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] ring-2 ring-[color:var(--color-brand)]/20'
          : 'border-[color:var(--color-line)] bg-white',
      ].join(' ')}
    >
      <div className="flex items-baseline justify-between gap-2">
        {/* `min-w-0` for the same reason as the finding rows: the room name is
            typed on a phone in somebody's hallway, and one without a space in
            it would otherwise hold this row open past the screen and take the
            page's scale with it (spec §31.10). */}
        <p className="min-w-0 text-sm font-medium">
          <span className="tabular text-[color:var(--color-muted)]">{time}</span>{' '}
          {t(`magicQuote.captureType.${capture.type}`)}
          {capture.roomName ? <span className="text-[color:var(--color-muted)]"> · {capture.roomName}</span> : null}
        </p>
        <CaptureStatusChip capture={capture} speechRecognitionAvailable={speechRecognitionAvailable} />
      </div>

      {capture.type === 'photo' && capture.mediaUrl ? (
        <div ref={photoRef}>
          <PhotoTrigger
            src={capture.mediaUrl}
            alt={t('magicQuote.timeline.photoAlt', { time })}
            group={`timeline-${sessionId}`}
            className="mt-2 block w-full"
          >
            <img
              src={capture.mediaUrl}
              alt={t('magicQuote.timeline.photoAlt', { time })}
              loading="lazy"
              className="h-24 w-full rounded-lg border border-[color:var(--color-line)] object-cover"
            />
          </PhotoTrigger>
        </div>
      ) : null}

      {capture.type === 'video' && capture.mediaUrl ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption -- the transcript below IS the caption
        <video
          // A callback ref, because one `useRef` cannot be the `ref` of both a
          // video and an audio element — their ref types are unrelated.
          ref={(element) => {
            mediaRef.current = element;
          }}
          src={capture.mediaUrl}
          controls
          preload="none"
          className="mt-2 w-full rounded-lg bg-[color:var(--color-ink)]"
        />
      ) : null}

      {capture.type === 'audio' && capture.mediaUrl ? (
        <audio
          ref={(element) => {
            mediaRef.current = element;
          }}
          src={openAtSeconds !== null ? `${capture.mediaUrl}#t=${openAtSeconds}` : capture.mediaUrl}
          controls
          preload="none"
          className="mt-2 w-full"
        />
      ) : null}

      {said ? (
        <p
          className={
            compact
              ? 'mt-1.5 line-clamp-2 text-sm text-[color:var(--color-ink)]'
              : 'mt-1.5 border-l-2 border-[color:var(--color-line)] pl-3 text-sm whitespace-pre-line text-[color:var(--color-ink)]'
          }
        >
          {said}
        </p>
      ) : null}

      {capture.error ? <p className="mt-1 text-sm text-[color:var(--color-danger)]">{capture.error}</p> : null}

      {/* A clip can be `processed` — its audio split fine — and still have
          lost the frames that carry everything the camera saw. Frames are
          never shown on their own, so this is the only place that failure
          can appear, and the Retry below re-runs them. */}
      {capture.failedFrameCount > 0 ? (
        <p className="mt-1 text-sm text-[color:var(--color-danger)]">
          {t('magicQuote.timeline.framesFailed', { count: capture.failedFrameCount })}
        </p>
      ) : null}

      {!compact ? (
        <div className="mt-2 flex gap-2">
          <Button variant="ghost" onClick={() => flag.mutate(!capture.flagged)}>
            {capture.flagged ? t('magicQuote.timeline.unflag') : t('magicQuote.timeline.flag')}
          </Button>
          {capture.status === 'failed' || capture.failedFrameCount > 0 ? (
            <Button variant="ghost" onClick={() => retry.mutate()} busy={retry.isPending}>
              {t('magicQuote.timeline.retry')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Where this capture is in the pipeline.
 *
 * A recording on a deployment with no speech recognition is called out
 * specifically rather than shown as processed: "we kept this but nobody will
 * ever read it back to you" is a materially different thing from "done", and
 * the estimator should know which one they have before they rely on it.
 */
function CaptureStatusChip({
  capture,
  speechRecognitionAvailable,
}: {
  capture: MagicQuoteCapture;
  speechRecognitionAvailable: boolean;
}) {
  const { t } = useTranslation();
  const isRecording = capture.type === 'audio' || capture.type === 'video';

  if (capture.status === 'failed') {
    return (
      <span className="badge shrink-0 border-[color:var(--color-danger)] bg-[color:var(--color-danger-soft)] text-[color:var(--color-danger)]">
        {t('magicQuote.captureStatus.failed')}
      </span>
    );
  }
  if (capture.status !== 'processed') {
    return (
      <span className="badge shrink-0 border-[color:var(--color-line)] text-[color:var(--color-muted)]">
        {/* A spinner rather than the word alone: this is the one status that
            is going to change on its own, and a still label reads as stuck. */}
        <span
          className="h-2.5 w-2.5 animate-spin rounded-full border-2 border-[color:var(--color-line)] border-t-[color:var(--color-muted)]"
          aria-hidden="true"
        />
        {t('magicQuote.captureStatus.processing')}
      </span>
    );
  }
  // Judged on whether words actually exist, not on whether the deployment
  // could in principle produce them. A clip past the transcription ceiling,
  // or one where nobody said anything, is exactly as un-transcribed as one on
  // a deployment with no speech recognition at all — and the estimator needs
  // to know that before relying on it either way.
  const isAudio = capture.type === 'audio';
  if (isRecording && !capture.transcription?.trim() && (isAudio || !speechRecognitionAvailable)) {
    return (
      <span className="badge shrink-0 border-[color:var(--color-warning)]/50 bg-[color:var(--color-warning-soft)] text-[color:var(--color-warning)]">
        {t('magicQuote.captureStatus.noTranscript')}
      </span>
    );
  }
  return (
    <span className="badge shrink-0 border-[color:var(--color-line)] text-[color:var(--color-muted)]">
      {t('magicQuote.captureStatus.processed')}
    </span>
  );
}
